import { ExternalLink, BadgeCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const PARTNERS = [
  { name: "Kaval Industries Ltd.", country: "Canada", category: "Valves & Actuators", authorized: true, website: "https://www.kaval.ca" },
  {
    name: "Portwest",
    country: "International",
    category: "Workwear & PPE",
    authorized: true,
    note: "Fastest growing workwear company in the world — designer, manufacturer and distributor of flame-resistant workwear, high-visibility workwear and PPE.",
    website: "https://www.portwest.com",
  },
  { name: "IGP Engineers Pvt. Ltd.", country: "India", category: "Insulation Kit Gaskets" },
  { name: "Zhejiang Huaxia Flow Technology Co., Ltd.", country: "China", category: "Valves" },
  { name: "Ningbo Dongxin High-Strength Nut Co., Ltd.", country: "China", category: "Studs & Fasteners" },
  { name: "Both-Well", country: "Taiwan", category: "Forged Carbon Steel Pipe Fittings", website: "https://www.bothwell.com.tw" },
];

const initials = (name) =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

export const Partners = () => {
  return (
    <section
      id="partners"
      data-testid="partners-section"
      className="bg-[#0B2545]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
        <Reveal className="max-w-2xl">
          <span className="font-mono text-xs font-medium tracking-[0.2em] uppercase text-amber-400">
            Global Sourcing
          </span>
          <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white">
            Brands &amp; Manufacturers We Source From
          </h2>
          <p className="mt-4 text-base sm:text-lg text-blue-100/80 leading-relaxed">
            We source and import products for the Egyptian market from a range
            of reputable international manufacturers and established global
            brands.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <div
            data-testid="partner-exxonmobil"
            className="mt-10 rounded-2xl border border-amber-400/30 bg-white/5 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5"
          >
            <span className="flex h-14 w-14 flex-none items-center justify-center rounded-xl bg-amber-500">
              <BadgeCheck className="h-7 w-7 text-[#0B2545]" />
            </span>
            <div>
              <p className="font-heading text-xl font-semibold text-white">
                Registered Contractor &amp; Vendor — ExxonMobil Egypt
              </p>
              <p className="mt-1 text-blue-100/80 leading-relaxed">
                A long-standing 30+ year business relationship, supporting
                ExxonMobil&apos;s procurement requirements with reliable
                products and international sourcing.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="mt-14 mb-6 font-mono text-xs tracking-[0.15em] uppercase text-amber-400">
            Manufacturers &amp; brands we source from include
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PARTNERS.map((p, i) => (
            <Reveal key={p.name} delay={0.08 * i}>
              <div
                data-testid={`partner-card-${i + 1}`}
                className="group h-full rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 transition-all duration-300 hover:-translate-y-1 hover:border-amber-400/40"
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-amber-500 font-heading text-lg font-bold text-[#0B2545]">
                    {initials(p.name)}
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-amber-400 border border-amber-400/30 rounded-full px-3 py-1">
                    {p.country}
                  </span>
                </div>
                <h3 className="mt-5 font-heading text-lg font-semibold text-white leading-snug">
                  {p.name}
                </h3>
                {p.category && (
                  <p className="mt-1.5 text-sm font-medium text-amber-300">
                    {p.category}
                  </p>
                )}
                {p.authorized && (
                  <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-400/40 px-3 py-1 text-[11px] font-semibold text-amber-300">
                    <BadgeCheck className="h-3.5 w-3.5" />
                    Authorized Distributor · Egypt
                  </span>
                )}
                {p.note && (
                  <p className="mt-3 text-sm text-blue-100/70 leading-relaxed">
                    {p.note}
                  </p>
                )}
                {p.website && (
                  <a
                    href={p.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`partner-link-${i + 1}`}
                    className="mt-3 inline-flex items-center gap-1 text-sm text-amber-300 hover:text-amber-200 transition-colors"
                  >
                    Visit website <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-12 rounded-xl border border-white/10 bg-white/5 p-6 max-w-3xl">
            <p className="font-heading text-sm font-semibold text-amber-400 uppercase tracking-wider">
              Brand Disclaimer
            </p>
            <p className="mt-3 text-xs text-blue-100/60 leading-relaxed">
              All product names, logos, and trademarks are the property of their
              respective owners and are used for identification purposes only.
            </p>
            <p className="mt-3 text-xs text-blue-100/60 leading-relaxed">
              Top Service is an authorized distributor of Portwest and Kaval in
              Egypt. For other brands, we source and import products directly
              from manufacturers and suppliers and do not claim to be an
              authorized or exclusive distributor, agent, or representative
              unless expressly stated.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
